import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/lib/AuthProvider";
import { ThemeProvider } from "@/lib/ThemeProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Wardrobe from "@/pages/Wardrobe";
import OutfitGenerator from "@/pages/OutfitGenerator";
import Stylist from "@/pages/Stylist";
import TryOn from "@/pages/TryOn";
import Shopping from "@/pages/Shopping";
import Community from "@/pages/Community";
import Profile from "@/pages/Profile";
import ProfileSettings from "@/pages/ProfileSettings";
import PersonalDetailsPage from "@/pages/PersonalDetailsPage";
import AIIdentityStylePage from "@/pages/AIIdentityStylePage";
import NotificationsSettings from "@/pages/NotificationsSettings";
import PrivacySettings from "@/pages/PrivacySettings";
import AppSettings from "@/pages/AppSettings";
import HelpPage from "@/pages/HelpPage";
import Family from "@/pages/Family";
import Travel from "@/pages/Travel";
import CalendarPage from "@/pages/Calendar";
import Plans from "@/pages/Plans";
import PublicProfile from "@/pages/PublicProfile";
import Collections from "@/pages/Collections";
import Admin from "@/pages/Admin";
import AtelierPicks from "@/pages/AtelierPicks";
import ResetPassword from "@/pages/ResetPassword";
import Tools from "@/pages/Tools";
import OutfitJudge from "@/pages/OutfitJudge";
import ComingSoon from "@/pages/ComingSoon";
import Shop from "@/pages/Shop";
import Stylists from "@/pages/Stylists";
import StylistProfile from "@/pages/StylistProfile";
import StylistStudio from "@/pages/StylistStudio";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Feedback from "@/pages/Feedback";
import Grievance from "@/pages/Grievance";
import DeleteAccount from "@/pages/DeleteAccount";
import BeautyDashboard from "@/pages/beauty/BeautyDashboard";
import BeautyScan from "@/pages/beauty/BeautyScan";
import BeautyReport from "@/pages/beauty/BeautyReport";
import BeautyMakeup from "@/pages/beauty/BeautyMakeup";
import BeautyHairBeard from "@/pages/beauty/BeautyHairBeard";
import BeautyCare from "@/pages/beauty/BeautyCare";
import BeautyHistory from "@/pages/beauty/BeautyHistory";
import BeautyCompare from "@/pages/beauty/BeautyCompare";
import BeautyRoutine from "@/pages/beauty/BeautyRoutine";
import BeautyIngredients from "@/pages/beauty/BeautyIngredients";

function AppRouter() {
  const location = useLocation();
  // Detect Emergent auth callback synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Public legal + feedback pages — reachable without auth for Play Store compliance */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/grievance" element={<Grievance />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireOnboarded={false}>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wardrobe"
        element={
          <ProtectedRoute>
            <Layout><Wardrobe /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/outfit"
        element={
          <ProtectedRoute>
            <Layout><OutfitGenerator /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stylist"
        element={
          <ProtectedRoute>
            <Layout><Stylist /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tryon"
        element={
          <ProtectedRoute>
            <Layout><TryOn /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shopping"
        element={
          <ProtectedRoute>
            <Layout><Shopping /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <Layout><Community /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/family"
        element={
          <ProtectedRoute>
            <Layout><Family /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travel"
        element={
          <ProtectedRoute>
            <Layout><Travel /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Layout><CalendarPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout><Profile /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/settings"
        element={
          <ProtectedRoute>
            <Layout><ProfileSettings /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/personal-details"
        element={
          <ProtectedRoute>
            <Layout><PersonalDetailsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/ai-identity"
        element={
          <ProtectedRoute>
            <Layout><AIIdentityStylePage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/help"
        element={
          <ProtectedRoute>
            <Layout><HelpPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/saved" element={<Navigate to="/collections" replace />} />
      <Route
        path="/profile/notifications"
        element={
          <ProtectedRoute>
            <Layout><NotificationsSettings /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/privacy"
        element={
          <ProtectedRoute>
            <Layout><PrivacySettings /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/app-settings"
        element={
          <ProtectedRoute>
            <Layout><AppSettings /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <Layout><Plans /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collections"
        element={
          <ProtectedRoute>
            <Layout><Collections /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/atelier"
        element={
          <ProtectedRoute>
            <Layout><AtelierPicks /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools"
        element={
          <ProtectedRoute>
            <Layout><Tools /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/outfit-judge"
        element={
          <ProtectedRoute>
            <Layout><OutfitJudge /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coming-soon"
        element={
          <ProtectedRoute>
            <Layout><ComingSoon /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grooming"
        element={<Navigate to="/beauty/hair-beard" replace />}
      />
      <Route
        path="/makeup"
        element={<Navigate to="/beauty/makeup" replace />}
      />
      <Route
        path="/beauty"
        element={
          <ProtectedRoute>
            <Layout><BeautyDashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/scan"
        element={
          <ProtectedRoute>
            <Layout><BeautyScan /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/scans/:scanId"
        element={
          <ProtectedRoute>
            <Layout><BeautyReport /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/makeup"
        element={
          <ProtectedRoute>
            <Layout><BeautyMakeup /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/hair-beard"
        element={
          <ProtectedRoute>
            <Layout><BeautyHairBeard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/care"
        element={
          <ProtectedRoute>
            <Layout><BeautyCare /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/history"
        element={
          <ProtectedRoute>
            <Layout><BeautyHistory /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/compare"
        element={
          <ProtectedRoute>
            <Layout><BeautyCompare /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/routine"
        element={
          <ProtectedRoute>
            <Layout><BeautyRoutine /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beauty/ingredients"
        element={
          <ProtectedRoute>
            <Layout><BeautyIngredients /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shop"
        element={
          <ProtectedRoute>
            <Layout><Shop /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stylists"
        element={
          <ProtectedRoute>
            <Layout><Stylists /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stylists/:stylistId"
        element={
          <ProtectedRoute>
            <Layout><StylistProfile /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stylist-studio"
        element={
          <ProtectedRoute>
            <Layout><StylistStudio /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Layout><Admin /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/u/:username"
        element={
          <ProtectedRoute>
            <Layout><PublicProfile /></Layout>
          </ProtectedRoute>
        }
      />
      {/* Catch-all: /@<username> routes — React Router v6 can't parametrize a
          segment that starts with '@', so we match every unrecognized URL and
          dispatch based on the pathname inside the component. */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Layout><PublicProfile /></Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="App">
            <Toaster richColors position="top-right" />
            <AppRouter />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
